import { SupportUserAvailabilityOverride } from "@redotech/crdb-enums/support-v2/support-user-availability-override";
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  GetShiftStatusesResponse,
  GetTeamWorkSchedulesResponse,
  GetWorkScheduleResponse,
  ShiftStatusView,
  UpdateWorkScheduleInput,
} from "@redotech/effect-api-shared/rpc/support-user-schedule";
import { useRequiredContext } from "@redotech/react-util/context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { EffectApiClientContext } from "../effect-api-client-provider";

/** Roots, not literals: a write invalidates one, and a near-miss matches none. */
const WORK_SCHEDULE = "workSchedule";
const SHIFT_STATUS = "shiftStatus";
const SHIFT_STATUSES = "shiftStatuses";
const TEAM_WORK_SCHEDULES = "teamWorkSchedules";

export const workScheduleQueryKey = (userId?: MongoUserId) =>
  [WORK_SCHEDULE, userId ?? "self"] as const;

/** The caller's own status. Distinct query from {@link shiftStatusesQueryKey}. */
export const shiftStatusQueryKey = () => [SHIFT_STATUS] as const;

/** A roster's statuses, keyed by the ids asked for. */
const shiftStatusesQueryKey = (mongoUserIds: readonly MongoUserId[]) =>
  [SHIFT_STATUSES, mongoUserIds] as const;

const teamWorkSchedulesQueryKey = (mongoUserIds: readonly MongoUserId[]) =>
  [TEAM_WORK_SCHEDULES, mongoUserIds] as const;

export function useWorkScheduleQuery(options?: {
  targetMongoUserId?: MongoUserId;
  enabled?: boolean;
}) {
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useQuery({
    queryKey: workScheduleQueryKey(options?.targetMongoUserId),
    enabled: options?.enabled !== false,
    queryFn: async (): Promise<typeof GetWorkScheduleResponse.Type> =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.getWorkSchedule({
            payload: { targetMongoUserId: options?.targetMongoUserId },
          })
          .pipe(Effect.orDie),
      ),
  });
}

export function useUpdateWorkScheduleMutation(targetMongoUserId?: MongoUserId) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: Omit<typeof UpdateWorkScheduleInput.Type, "targetMongoUserId">,
    ) =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.updateWorkSchedule({
            payload: { ...input, targetMongoUserId },
          })
          .pipe(Effect.orDie),
      ),
    onSuccess: async () => {
      /** Hours move who is on shift, so every status and roster view is stale. */
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workScheduleQueryKey(targetMongoUserId),
        }),
        queryClient.invalidateQueries({ queryKey: [TEAM_WORK_SCHEDULES] }),
        queryClient.invalidateQueries({ queryKey: shiftStatusQueryKey() }),
        queryClient.invalidateQueries({ queryKey: [SHIFT_STATUSES] }),
      ]);
    },
  });
}

/** Polls so the presence dot follows the schedule without a reload. */
export function useShiftStatusQuery(options?: { enabled?: boolean }) {
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useQuery({
    queryKey: shiftStatusQueryKey(),
    enabled: options?.enabled !== false,
    refetchInterval: 60_000,
    queryFn: async (): Promise<typeof ShiftStatusView.Type> =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.getMyShiftStatus()
          .pipe(Effect.orDie),
      ),
  });
}

/** Polls alongside the roster so the dots follow each user's schedule. */
export function useShiftStatusesQuery(mongoUserIds: readonly MongoUserId[]) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const sortedIds = [...mongoUserIds].sort();

  return useQuery({
    queryKey: shiftStatusesQueryKey(sortedIds),
    enabled: sortedIds.length > 0,
    refetchInterval: 60_000,
    queryFn: async (): Promise<typeof GetShiftStatusesResponse.Type> =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.getShiftStatuses({
            payload: { mongoUserIds: sortedIds },
          })
          .pipe(Effect.orDie),
      ),
  });
}

/**
 * Whole-team configured hours in one call, for the manager coverage calendar.
 * Requires MANAGE_TEAM_USERS; without it the API answers NotFound rather than
 * disclosing that the roster exists, so pass `enabled: false` for non-managers.
 */
export function useTeamWorkSchedulesQuery(
  mongoUserIds: readonly MongoUserId[],
  options?: { enabled?: boolean },
) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const sortedIds = [...mongoUserIds].sort();

  return useQuery({
    queryKey: teamWorkSchedulesQueryKey(sortedIds),
    enabled: options?.enabled !== false && sortedIds.length > 0,
    queryFn: async (): Promise<typeof GetTeamWorkSchedulesResponse.Type> =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.getTeamWorkSchedules({
            payload: { mongoUserIds: sortedIds },
          })
          .pipe(Effect.orDie),
      ),
  });
}

/**
 * `channels: null` restores every channel. Omit `targetMongoUserId` to write
 * your own row; targeting another agent requires MANAGE_TEAM_USERS.
 */
export function useUpdateAssignableChannelsMutation(
  targetMongoUserId?: MongoUserId,
) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channels: readonly string[] | null) =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.updateAssignableChannels({
            payload: { channels, targetMongoUserId },
          })
          .pipe(Effect.orDie),
      ),
    onSuccess: async () => {
      /** Channels ride on the schedule and the roster, not on one's own status. */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [WORK_SCHEDULE] }),
        queryClient.invalidateQueries({ queryKey: [TEAM_WORK_SCHEDULES] }),
        queryClient.invalidateQueries({ queryKey: [SHIFT_STATUSES] }),
      ]);
    },
  });
}

/**
 * Omit `targetMongoUserId` to toggle your own availability; targeting another
 * agent requires MANAGE_TEAM_USERS.
 */
export function useSetAvailabilityOverrideMutation(
  targetMongoUserId?: MongoUserId,
) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (status: SupportUserAvailabilityOverride | null) =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .supportUserSchedule.setAvailabilityOverride({
            payload: { status, targetMongoUserId },
          })
          .pipe(Effect.orDie),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: shiftStatusQueryKey() }),
        queryClient.invalidateQueries({ queryKey: [WORK_SCHEDULE] }),
        queryClient.invalidateQueries({ queryKey: [TEAM_WORK_SCHEDULES] }),
        queryClient.invalidateQueries({ queryKey: [SHIFT_STATUSES] }),
      ]);
    },
  });
}
